import './OrganizationWidgetModal.css';

import { useContext, useState } from 'react';
import { useLoginContext } from '../../../App';
import { B2BContentContext } from '../../layout/Layout';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import useGettingCustomDashboards from '../hooks/useGettingCustomDashboards';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import B2BStepper from '../../b2b-common/B2BStepper';
import WidgetSelector from '../components/WidgetSelector';
import WidgetConfigurator from '../components/widget-configuration/WidgetConfigurator';
import { getFirstAvailableCell } from './AreaIndicatorWidgetModal';
import { showNotification } from '../../b2b-common/Notification';
import DashboardSelector from '../components/DashboardSelector';
import { WidgetSizeSelector } from './EditWidgetModal';
import WidgetCellSelector from '../components/WidgetCellSelector';
import { saveNewWidget } from '../../../services/b2b-reporting-apiServices';

const OrganizationWidgetModal = ({ open, onCancel, onSaved }) => {
    const { loginData } = useLoginContext();
    const organization = {
        organizationUUID: loginData?.loggedOrganizationUuid || null,
        locale: loginData?.locale || 'es',
    }
    const { setNotification } = useContext(B2BContentContext);
    const { t } = useB2BTranslation('b2b-reporting');
    const { customDashboards, isLoading } = useGettingCustomDashboards();
    const [selectedWidgetTemplate, setSelectedWidgetTemplate] = useState(null);
    const [selectedWidgetConfig, setSelectedWidgetConfig] = useState(null);
    const dashboardsData = { content: customDashboards };
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [page, setPage] = useState(1);
    const [selectedCell, setSelectedCell] = useState(null);

    const handleSelectedWidgetTemplate = (widgetTemplate) => {
        setSelectedWidgetTemplate(widgetTemplate);
    }

    const handleSelectedWidgetConfig = (widgetConfig) => {
        setSelectedWidgetConfig(widgetConfig);
        // setCurrentWidget(widgetConfig);
    }

    const handleSaveNewWidget = async () => {
        try {
            const savedDashboard = await saveNewWidget(loginData.accessToken, loginData.loggedOrganizationUuid, selectedDashboard.dashboardId, selectedWidgetConfig);
            showNotification(setNotification, t('Widget saved successfully'), 'success', 3000);
            onSaved && onSaved(savedDashboard);
        } catch (error) {
            console.error('Error saving widget:', error);
            showNotification(setNotification, t('Error saving widget') + ': ' + error.message, 'error', 3000);
            onCancel && onCancel();
        }
    }

    const handleCancelNewWidget = () => {
        onCancel && onCancel();
    }

    const handleSelectedDashboard = (dashboard) => {
        setSelectedDashboard(dashboard);
        const availableCell = getFirstAvailableCell(dashboard);
        if (availableCell) {
            setSelectedWidgetConfig(prevWidget => ({
                ...prevWidget,
                widgetConfig: {
                    ...prevWidget.widgetConfig,
                    row: availableCell.row,
                    column: availableCell.col,
                    width: 1,
                    height: 1
                }
            }));
            setSelectedCell(availableCell);
        } else {
            setSelectedCell(null);
            setSelectedDashboard(null);
            showNotification(setNotification, t('No available cell found in the selected dashboard'), 'warning', 5000);
        }
    }

    const handleSelectedCell = (cell) => {
        setSelectedCell(cell);
        setSelectedWidgetConfig(prevWidget => ({
            ...prevWidget,
            widgetConfig: {
                ...prevWidget.widgetConfig,
                row: cell.row,
                column: cell.col,
                width: cell.width,
                height: cell.height
            }
        }));
    }

    const steps = [
        {
            label: t('Select Organization Widget'),
            description: t('Select the organization widget you want to add to the dashboard'),
            content: (
                <WidgetSelector
                    dto={organization}
                    scope={'ORGANIZATION'}
                    selectedTemplate={selectedWidgetTemplate}
                    onSelectWidgetTemplate={handleSelectedWidgetTemplate} />
            ),
        },
        {
            label: t('Configure the selected widget'),
            description: t('Depending on the selected widget you will be required to configure some aspects of it.'),
            content: (
                <WidgetConfigurator
                    dto={organization}
                    widgetTemplate={selectedWidgetTemplate}
                    onWidgetConfigurationChange={handleSelectedWidgetConfig}
                />
            ),
            disableNext: (!selectedWidgetConfig) ? true : false,
        },
        {
            label: !selectedDashboard ? t('Select Dashboard') : t('Selected Dashboard') + `: ${selectedDashboard.dashboardName}`,
            description: t('Select the dashboard where you want to add the widget.'),
            content: (<DashboardSelector dashboards={dashboardsData} page={page}
                selectedDashboard={selectedDashboard}
                setPage={setPage} onSelectedDashboard={handleSelectedDashboard} />),
            disableNext: (!selectedDashboard) ? true : false,
        },
        {
            label: t('Place the Widget'),
            description: t('Select the cell where you want to place the widget and also the size of it.'),
            content: (
                <div className='widget-placement'>
                    <WidgetSizeSelector widget={selectedWidgetConfig} setWidget={setSelectedWidgetConfig} />
                    <WidgetCellSelector dashboard={selectedDashboard} widget={selectedWidgetConfig} onSelectedCell={handleSelectedCell} />
                </div>
            ),
            disableNext: (!selectedCell) ? true : false,
        },
    ]

    return (
        <B2BPopUpForm
            open={open}
            title={t('Add Widget to Dashboard')}
            onClose={onCancel}
            className={'organization-widget-modal b2b-light-01'}
        >
            <B2BStepper
                steps={steps}
                className={'organization-widget-stepper'}
                onSuccess={handleSaveNewWidget}
                onCancel={handleCancelNewWidget}
            />
        </B2BPopUpForm>
    );
}

export default OrganizationWidgetModal;